  import {
    Injectable,
    Renderer2,
    RendererFactory2,
    effect,
    signal,
    Inject,
    PLATFORM_ID,
    OnDestroy,
    RendererStyleFlags2,
    NgZone,
  } from '@angular/core';
  import { isPlatformBrowser } from '@angular/common';
  import { Subject, Subscription } from 'rxjs';
  import { debounceTime } from 'rxjs/operators';
  import { PreferenceService } from '../shared/services/preference.service';
  import { AppService } from '../shared/services/app.service';

  // ============================================================
  // ENUMS
  // ============================================================

  export enum AccessibilityFeature {
    HighContrast = 'highContrast',
    InvertColors = 'invertColors',
    Monochrome = 'monochrome',
    Saturation = 'saturation',
    Brightness = 'brightness',
    DarkMode = 'darkMode',
    BiggerText = 'biggerText',
    TextSpacing = 'textSpacing',
    LetterSpacing = 'letterSpacing',
    LineHeight = 'lineHeight',
    TextAlign = 'textAlign',
    DyslexiaFont = 'dyslexiaFont',
    FontFamily = 'fontFamily',
    ReadingGuide = 'readingGuide',
    ReadingMask = 'readingMask',
    HighlightLinks = 'highlightLinks',
    HideImages = 'hideImages',
    PauseAnimations = 'pauseAnimations',
    KeyboardNavigation = 'keyboardNavigation',
    FocusHighlight = 'focusHighlight',
    ScreenReaderImprovements = 'screenReaderImprovements',
    CursorSize = 'cursorSize',
    CursorColor = 'cursorColor',
    ZoomLevel = 'zoomLevel',
    BoldInputText = 'boldInputText',
  }

  export enum ThemeMode {
    Light = 'light',
    Dark = 'dark',
    Auto = 'auto',
  }

  export enum TextAlignMode {
    Default = 'default',
    Left = 'left',
    Center = 'center',
    Right = 'right',
    Justify = 'justify',
  }

  export enum FontFamilyOption {
    Default = 'default',
    SansSerif = 'sans-serif',
    Serif = 'serif',
    Monospace = 'monospace',
    Dyslexic = 'dyslexic',
  }

  export enum CursorColorOption {
    Default = 'default',
    Black = 'black',
    White = 'white',
    Yellow = 'yellow',
    Blue = 'blue',
  }

  /** 0–5 scale reused by every incremental feature (text size, spacing, zoom, etc.) */
  export enum StepLevel {
    Off = 0,
    Level1 = 1,
    Level2 = 2,
    Level3 = 3,
    Level4 = 4,
    Level5 = 5,
  }

  // ============================================================
  // MODEL
  // ============================================================

  export interface AccessibilitySettings {
    highContrast: boolean;
    invertColors: boolean;
    monochrome: boolean;
    saturation: StepLevel;
    brightness: StepLevel;
    themeMode: ThemeMode;

    biggerText: StepLevel;
    textSpacing: StepLevel;
    letterSpacing: StepLevel;
    lineHeight: StepLevel;
    textAlign: TextAlignMode;
    dyslexiaFont: boolean;
    fontFamily: FontFamilyOption;

    readingGuide: boolean;
    readingMask: boolean;
    highlightLinks: boolean;
    hideImages: boolean;
    pauseAnimations: boolean;

    keyboardNavigation: boolean;
    focusHighlight: boolean;
    screenReaderImprovements: boolean;

    cursorSize: StepLevel;
    cursorColor: CursorColorOption;
    zoomLevel: number;
    boldInputText: boolean;
  }

  export const DEFAULT_ACCESSIBILITY_SETTINGS: Readonly<AccessibilitySettings> = {
    highContrast: false,
    invertColors: false,
    monochrome: false,
    saturation: StepLevel.Off,
    brightness: StepLevel.Off,
    themeMode: ThemeMode.Light,

  biggerText: StepLevel.Off,
  textSpacing: StepLevel.Off,
  letterSpacing: StepLevel.Off,
  lineHeight: StepLevel.Off,
  textAlign: TextAlignMode.Default,
  dyslexiaFont: false,
  fontFamily: FontFamilyOption.Default,

  readingGuide: false,
  readingMask: false,
  highlightLinks: false,
  hideImages: false,
  pauseAnimations: false,

  keyboardNavigation: false,
  focusHighlight: false,
  screenReaderImprovements: false,

  cursorSize: StepLevel.Off,
  cursorColor: CursorColorOption.Default,
  zoomLevel: 0,
  boldInputText: false
};

/** Which UI control a card renders. Drives the html loop instead of hardcoding 24 cards. */
export type FeatureControlType =
  | 'toggle'
  | 'step'
  | 'theme'
  | 'textAlign'
  | 'fontFamily'
  | 'cursorColor';

export interface FeatureCardConfig {
  feature: AccessibilityFeature;
  key: keyof AccessibilitySettings;
  label: string;
  icon: string;
  controlType: FeatureControlType;
  minStep?: number;
  maxStep?: number;
}

export const ACCESSIBILITY_FEATURE_CARDS: readonly FeatureCardConfig[] = [
  { feature: AccessibilityFeature.BoldInputText, key: 'boldInputText', label: 'Bold Text', icon: 'fa-bold', controlType: 'toggle' },
  //{ feature: AccessibilityFeature.HighContrast, key: 'highContrast', label: 'High Contrast', icon: 'fa-adjust', controlType: 'toggle' },
  { feature: AccessibilityFeature.InvertColors, key: 'invertColors', label: 'Invert Colors', icon: 'fa fa-adjust', controlType: 'toggle' },
  { feature: AccessibilityFeature.Monochrome, key: 'monochrome', label: 'Monochrome', icon: 'fa fa-tint', controlType: 'toggle' },
  //{ feature: AccessibilityFeature.Saturation, key: 'saturation', label: 'Saturation', icon: 'fa-fill-drip', controlType: 'step' },
  { feature: AccessibilityFeature.Brightness, key: 'brightness', label: 'Brightness', icon: 'fa fa-sun-o', controlType: 'step', minStep: -5, maxStep: 5},
  //{ feature: AccessibilityFeature.DarkMode, key: 'themeMode', label: 'Theme', icon: 'fa-moon', controlType: 'theme' },
  { feature: AccessibilityFeature.BiggerText, key: 'biggerText', label: 'Bigger Text', icon: 'fa fa-text-height', controlType: 'step', minStep: -2, maxStep: 2 },
  { feature: AccessibilityFeature.TextSpacing, key: 'textSpacing', label: 'Text Space', icon: 'fa-text-width', controlType: 'step', minStep: -5, maxStep: 5 },
  { feature: AccessibilityFeature.LetterSpacing, key: 'letterSpacing', label: 'Letter Space', icon: 'fa fa-text-width', controlType: 'step' },
  { feature: AccessibilityFeature.LineHeight, key: 'lineHeight', label: 'Line Height', icon: 'fa-align-justify', controlType: 'step' },
  //{ feature: AccessibilityFeature.TextAlign, key: 'textAlign', label: 'Text Align', icon: 'fa-align-center', controlType: 'textAlign' },
  //{ feature: AccessibilityFeature.DyslexiaFont, key: 'dyslexiaFont', label: 'Dyslexia Friendly', icon: 'fa-font', controlType: 'toggle' },
  { feature: AccessibilityFeature.FontFamily, key: 'fontFamily', label: 'Font Family', icon: 'fa fa-font', controlType: 'fontFamily' },
  { feature: AccessibilityFeature.ReadingGuide, key: 'readingGuide', label: 'Reading Guide', icon: 'fa fa-book', controlType: 'toggle' },
  { feature: AccessibilityFeature.ReadingMask, key: 'readingMask', label: 'Reading Mask', icon: 'fa fa-eye', controlType: 'toggle' },
  //{ feature: AccessibilityFeature.HighlightLinks, key: 'highlightLinks', label: 'Highlight Links', icon: 'fa-link', controlType: 'toggle' },
  //{ feature: AccessibilityFeature.HideImages, key: 'hideImages', label: 'Hide Images', icon: 'fa-image', controlType: 'toggle' },
  //{ feature: AccessibilityFeature.PauseAnimations, key: 'pauseAnimations', label: 'Pause Animations', icon: 'fa-pause', controlType: 'toggle' },
  //{ feature: AccessibilityFeature.KeyboardNavigation, key: 'keyboardNavigation', label: 'Keyboard Nav', icon: 'fa-keyboard', controlType: 'toggle' },
  //{ feature: AccessibilityFeature.FocusHighlight, key: 'focusHighlight', label: 'Focus Highlight', icon: 'fa-crosshairs', controlType: 'toggle' },
  //{ feature: AccessibilityFeature.ScreenReaderImprovements, key: 'screenReaderImprovements', label: 'Screen Reader', icon: 'fa-assistive-listening-systems', controlType: 'toggle' },
  //{ feature: AccessibilityFeature.CursorSize, key: 'cursorSize', label: 'Cursor Size', icon: 'fa-mouse-pointer', controlType: 'step' },
  //{ feature: AccessibilityFeature.CursorColor, key: 'cursorColor', label: 'Cursor Color', icon: 'fa-eye-dropper', controlType: 'cursorColor' },
  { feature: AccessibilityFeature.ZoomLevel, key: 'zoomLevel', label: 'Zoom', icon: 'fa fa-search-plus', controlType: 'step', minStep: -1, maxStep: 3 },
];

/** Zoom factor per step (step 0 = off). Steps beyond the range fall back to the nearest end. */
const ZOOM_FACTORS: Record<number, number> = { [-1]: 0.75, 0: 1, 1: 1.1, 2: 1.2, 3: 1.3 };

// ============================================================
// CONSTANTS
// ============================================================

const STORAGE_KEY = 'kx_accessibility_settings_v1';
const PREFERENCE_KEY = 'accessibility_settings';

/** Half-height of the reading mask's clear band, in px — the aperture is 2× this, centred on the pointer. */
const MASK_APERTURE_HALF = 60;

// ============================================================
// SERVICE
// ============================================================

@Injectable({ providedIn: 'root' })
export class AccessibilityService implements OnDestroy {
  private readonly renderer: Renderer2;
  private readonly isBrowser: boolean;

  private guideEl: HTMLElement | null = null;
  private guideUnlisten: (() => void) | null = null;

  private maskTopEl: HTMLElement | null = null;
  private maskBottomEl: HTMLElement | null = null;
  private maskUnlisten: (() => void) | null = null;

  /** Last known pointer Y, seeded to the viewport centre. The guide and the mask used to learn
   * a position only from `mousemove`, so an effect restored from saved settings on page load
   * sat at whatever the stylesheet's initial geometry happened to be until the user moved the
   * mouse — see #201077 and the .kx-reading-mask-* rules in styles.scss. */
  private pointerY = typeof window !== 'undefined' ? Math.round(window.innerHeight / 2) : 0;

  private readonly _settings = signal<AccessibilitySettings>(this.loadInitialSettings());
  readonly settings = this._settings.asReadonly();

  /** True while the accessibility panel itself is open — set from AccessibilityComponent's
   * own ngOnInit/ngOnDestroy via openPanel()/closePanel(), since that component's lifecycle
   * is the real open/close signal (the panel is mounted/unmounted by base.component.ts's
   * asideBarService, not by togglePanel()). Drives body.kx-a11y-panel-open (styles.scss),
   * which re-elevates #aside-modal-container above the color-filter overlay and cancels the
   * inherited zoom while the panel exists. */
  readonly isPanelOpen = signal<boolean>(false);

  /** True while a debounced auto-save to user_preference is in flight — drives the saving spinner on the card that triggered it. */
  readonly isSaving = signal<boolean>(false);
  /** Key of the setting whose card most recently changed — lets the template show the spinner on that one card instead of everywhere. */
  readonly lastChangedKey = signal<keyof AccessibilitySettings | null>(null);
  private readonly saveTrigger$ = new Subject<void>();
  private saveTriggerSub: Subscription | null = null;

  constructor(
    rendererFactory: RendererFactory2,
    private readonly preferenceService: PreferenceService,
    private readonly appService: AppService,
    private readonly ngZone: NgZone,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.renderer = rendererFactory.createRenderer(null, null);

    if (this.isBrowser) {
      this.fetchRemoteSettings();

      // Runs once immediately, then re-runs whenever _settings changes —
      // applies the live preview and caches locally. Remote persistence is
      // debounced separately (see queueSave()) so rapid clicks (e.g. holding
      // the +/- step buttons) don't fire a request per click.
      effect(() => {
        const current = this._settings();
        this.applyToDom(current);
        this.persistLocal(current);
      });

      this.saveTriggerSub = this.saveTrigger$
        .pipe(debounceTime(600))
        .subscribe(() => this.performSave());

      // See the doc comment on isPanelOpen — this is the single place that
      // toggles body.kx-a11y-panel-open, which styles.scss uses to re-elevate
      // and un-zoom #aside-modal-container specifically while this panel is open.
      effect(() => {
        this.setClass(document.body, 'kx-a11y-panel-open', this.isPanelOpen());
      });
    }
  }

  ngOnDestroy(): void {
    this.saveTriggerSub?.unsubscribe();
    // Tear down by class, not by cached reference — see reconcileEffectEl(). An element left
    // behind here is exactly what made "Reset all" look like it did nothing (#201077). These
    // also drop the mousemove listeners.
    this.toggleColorFilterOverlay(false);
    this.toggleReadingGuide(false);
    this.toggleReadingMask(false);
  }

  // ---------------- Panel ----------------

  togglePanel(): void {
    this.isPanelOpen.update((v) => !v);
  }

  // Called from AccessibilityComponent.ngOnInit() — see the doc comment on isPanelOpen.
  openPanel(): void {
    // Re-assert the DOM from the current settings on every open. applyToDom() otherwise runs
    // only when _settings changes, so once anything outside this service had disturbed one of
    // the effect elements the page stayed wrong indefinitely while the panel kept rendering
    // the feature as "on" — the invert half of #201077. reconcileEffectEl() makes this cheap
    // and idempotent: nothing is recreated if the DOM already matches.
    this.applyToDom(this._settings());
    this.isPanelOpen.set(true);
  }

  closePanel(): void {
    this.isPanelOpen.set(false);
  }

  // ---------------- Mutators ----------------

  toggleBoolean(key: keyof AccessibilitySettings): void {
    this._settings.update((s) => ({ ...s, [key]: !s[key] }) as AccessibilitySettings);
    this.lastChangedKey.set(key);
    this.queueSave();
  }

  incrementStep(key: keyof AccessibilitySettings, max: number = StepLevel.Level5): void {
    const value = this._settings()[key] as unknown as number;
    const next = Math.min(value + 1, max);
    if (next === value) return; // already at the max step — nothing changed, don't fire a save
    this._settings.update((s) => ({ ...s, [key]: next }));
    this.lastChangedKey.set(key);
    this.queueSave();
  }

  decrementStep(key: keyof AccessibilitySettings, min: number = StepLevel.Off): void {
    const value = this._settings()[key] as unknown as number;
    const next = Math.max(value - 1, min);
    if (next === value) return; // already at the min step — nothing changed, don't fire a save
    this._settings.update((s) => ({ ...s, [key]: next }));
    this.lastChangedKey.set(key);
    this.queueSave();
  }

  setThemeMode(mode: ThemeMode): void {
    if (this._settings().themeMode === mode) return;
    this._settings.update((s) => ({ ...s, themeMode: mode }));
    this.lastChangedKey.set('themeMode');
    this.queueSave();
  }

  setTextAlign(mode: TextAlignMode): void {
    if (this._settings().textAlign === mode) return;
    this._settings.update((s) => ({ ...s, textAlign: mode }));
    this.lastChangedKey.set('textAlign');
    this.queueSave();
  }

  setFontFamily(font: FontFamilyOption): void {
    if (this._settings().fontFamily === font) return;
    this._settings.update((s) => ({ ...s, fontFamily: font }));
    this.lastChangedKey.set('fontFamily');
    this.queueSave();
  }

  setCursorColor(color: CursorColorOption): void {
    if (this._settings().cursorColor === color) return;
    this._settings.update((s) => ({ ...s, cursorColor: color }));
    this.lastChangedKey.set('cursorColor');
    this.queueSave();
  }

  resetAll(): void {
    this._settings.set({ ...DEFAULT_ACCESSIBILITY_SETTINGS });
    this.lastChangedKey.set(null);
    this.queueSave();
  }

  // ---------------- Persistence ----------------

  private loadInitialSettings(): AccessibilitySettings {
    if (typeof localStorage === 'undefined') {
      return { ...DEFAULT_ACCESSIBILITY_SETTINGS };
    }
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { ...DEFAULT_ACCESSIBILITY_SETTINGS };
      const parsed = JSON.parse(raw) as Partial<AccessibilitySettings>;
      return { ...DEFAULT_ACCESSIBILITY_SETTINGS, ...parsed };
    } catch {
      return { ...DEFAULT_ACCESSIBILITY_SETTINGS };
    }
  }

  private persistLocal(settings: AccessibilitySettings): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      /* storage unavailable — signal remains the source of truth for this session */
    }
  }

  /** DB settings win over localStorage — applied on login (see login-service.service.ts) and on app init if a session already exists. */
  private fetchRemoteSettings(): void {
    if (!this.appService.getSessionId()) return;
    this.loadUserPreference();
  }

  /** Fetches the saved accessibility settings from user_preference and applies them. Safe to call again after login. */
  loadUserPreference(): void {
    this.preferenceService.getUserPreferenceConfig([PREFERENCE_KEY], (data: any) => {
      const remote = data?.[PREFERENCE_KEY];
      if (remote && Object.keys(remote).length > 0) {
        this._settings.update((s) => ({ ...s, ...remote }));
      }
    });
  }

  /** Queues a debounced auto-save — called from every mutator so a burst of clicks (e.g. holding +/-) collapses into one request. */
  private queueSave(): void {
    if (!this.isBrowser) return;
    this.saveTrigger$.next();
  }

  /** Persists the current settings to user_preference (account-management) the same way editable-grid saves its column config, so it reloads on next login. Drives the panel's moving-dot "Saving…" indicator. */
  private performSave(): void {
    if (!this.appService.getSessionId()) return;
    this.isSaving.set(true);
    this.preferenceService.saveTableConfigData(PREFERENCE_KEY, this._settings(), () => this.isSaving.set(false));
  }

  // ---------------- DOM application (Renderer2 + CSS variables only) ----------------

  private applyToDom(settings: AccessibilitySettings): void {
    const root = document.documentElement;

    this.setClass(root, 'kx-high-contrast', settings.highContrast);
    this.setClass(root, 'kx-invert', settings.invertColors);
    this.setClass(root, 'kx-monochrome', settings.monochrome);
    this.toggleColorFilterOverlay(
      settings.highContrast || settings.invertColors || settings.monochrome ||
      settings.saturation !== StepLevel.Off || settings.brightness !== StepLevel.Off,
    );
    this.setClass(root, 'kx-dyslexia-font', settings.dyslexiaFont);
    this.setClass(root, 'kx-highlight-links', settings.highlightLinks);
    this.setClass(root, 'kx-hide-images', settings.hideImages);
    this.setClass(root, 'kx-pause-animations', settings.pauseAnimations);
    this.setClass(root, 'kx-keyboard-nav', settings.keyboardNavigation);
    this.setClass(root, 'kx-focus-highlight', settings.focusHighlight);
    this.setClass(root, 'kx-sr-improvements', settings.screenReaderImprovements);
    this.setClass(root, 'kx-bold-input', settings.boldInputText);
    // Gates the `zoom` rule in styles.scss — the non-standard `zoom` property has
    // known quirks compounding with OS/browser display scaling even at its identity
    // value (1), so only attach it while Bigger Text or Zoom is actually non-default.
    this.setClass(root, 'kx-text-scale-on', settings.biggerText !== StepLevel.Off);
    this.setClass(root, 'kx-zoom-on', settings.zoomLevel !== 0);
    // Gates the line-height/word-spacing/letter-spacing rule in styles.scss —
    // --kx-line-height's "off" value (1.2) isn't a true match for the app's
    // real baseline line-height (Bootstrap body: 1.5), so leaving it
    // unconditional was compressing line spacing app-wide at all times.
    this.setClass(
      root,
      'kx-text-spacing-on',
      settings.textSpacing !== StepLevel.Off ||
      settings.letterSpacing !== StepLevel.Off ||
      settings.lineHeight !== StepLevel.Off,
    );

    this.renderer.setAttribute(root, 'data-kx-theme', this.resolveThemeMode(settings.themeMode));
    this.renderer.setAttribute(root, 'data-kx-text-align', settings.textAlign);
    this.renderer.setAttribute(root, 'data-kx-font-family', settings.fontFamily);
    this.renderer.setAttribute(root, 'data-kx-cursor-color', settings.cursorColor);

    this.setVar(root, '--kx-font-scale', `${1 + settings.biggerText * 0.1}`);
    this.setVar(root, '--kx-text-spacing', `${settings.textSpacing * 0.05}em`);
    this.setVar(root, '--kx-letter-spacing', `${settings.letterSpacing * 0.03}em`);
    this.setVar(root, '--kx-line-height', `${1.2 + settings.lineHeight * 0.15}`);
    this.setVar(root, '--kx-saturation', `${100 + settings.saturation * 20}%`);
    this.setVar(root, '--kx-brightness', `${100 + settings.brightness * 10}%`);
    this.setVar(root, '--kx-cursor-scale', `${1 + settings.cursorSize * 0.5}`);
    this.setVar(root, '--kx-zoom', `${ZOOM_FACTORS[settings.zoomLevel] ?? (settings.zoomLevel > 0 ? 1.3 : 0.75)}`);

    this.toggleReadingGuide(settings.readingGuide);
    this.toggleReadingMask(settings.readingMask);
  }

  // CSS `filter` (even at identity values) establishes a new containing block for every
  // `position: fixed` descendant of the filtered element — applying it to app-header/
  // app-sidebar/main/app-footer broke every fixed-position bottom action bar nested inside
  // them (e.g. payment-detail.component.scss's submit bar) the moment a filter was actually
  // active. That pattern recurs across many screens, so no ancestor selector fixes it.
  // `backdrop-filter` has no such effect: it only visually distorts whatever renders behind
  // the element, so applying it to a single non-ancestor full-viewport overlay dims/inverts/
  // grayscales the page without ever touching any real component's containing block. See
  // .kx-a11y-color-overlay in styles.scss for why its z-index is high enough to also dim the
  // header/dropdowns/grid context menus, and for the accessibility panel's re-elevation.
  private toggleColorFilterOverlay(active: boolean): void {
    this.reconcileEffectEl('kx-a11y-color-overlay', active);
  }

  /**
   * Creates/removes one of the body-level effect elements, treating the DOM — not a cached
   * field — as the source of truth.
   *
   * The old toggles guarded on `this.<x>El`, which desynchronises the instant anything else
   * touches these nodes, and nothing ever noticed: applyToDom() re-runs only when _settings
   * changes. Both #201077 symptoms come from that. A second AccessibilityService instance
   * appends a *second* .kx-a11y-color-overlay, and two stacked `backdrop-filter: invert(1)`
   * layers cancel out exactly — the page renders normally while the card still reads "on".
   * Mirror case: whichever instance did not create the reading mask holds a null reference, so
   * its "Reset all" removes nothing and the mask stays on screen.
   *
   * Adopting an element that already exists — and deleting every duplicate, and every match on
   * removal — makes both impossible however many instances or raw DOM rewrites are in play.
   * base.module.ts no longer double-registers the service either, but this is the half that
   * has to be robust, because it is the half the user actually sees.
   */
  private reconcileEffectEl(marker: string, active: boolean, extraClasses: string[] = []): HTMLElement | null {
    const found = Array.from(document.querySelectorAll<HTMLElement>(`.${marker}`));
    if (!active) {
      found.forEach((el) => this.renderer.removeChild(el.parentNode ?? document.body, el));
      return null;
    }
    // Anything past the first is a leak from another instance — keep one, drop the rest.
    found.slice(1).forEach((el) => this.renderer.removeChild(el.parentNode ?? document.body, el));
    if (found.length) return found[0];

    const el: HTMLElement = this.renderer.createElement('div');
    for (const cls of [marker, ...extraClasses]) this.renderer.addClass(el, cls);
    this.renderer.appendChild(document.body, el);
    return el;
  }

  private setClass(el: HTMLElement, className: string, active: boolean): void {
    if (active) this.renderer.addClass(el, className);
    else this.renderer.removeClass(el, className);
  }

  private setVar(el: HTMLElement, name: string, value: string): void {
    this.renderer.setStyle(el, name, value, RendererStyleFlags2.DashCase);
  }

  private resolveThemeMode(mode: ThemeMode): 'light' | 'dark' {
    if (mode === ThemeMode.Auto) {
      const prefersDark =
        typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
      return prefersDark ? 'dark' : 'light';
    }
    return mode === ThemeMode.Dark ? 'dark' : 'light';
  }

  private toggleReadingGuide(active: boolean): void {
    this.guideEl = this.reconcileEffectEl('kx-reading-guide-line', active);
    if (!this.guideEl) {
      this.guideUnlisten?.();
      this.guideUnlisten = null;
      return;
    }
    // Place it from the last known pointer position instead of leaving it wherever the
    // stylesheet's initial geometry puts it until the first mousemove — on a page load that
    // restores saved settings there hasn't been one yet (#201077).
    this.renderer.setStyle(this.guideEl, 'top', `${this.pointerY}px`);
    if (this.guideUnlisten) return;
    // Runs outside Angular's zone: this only writes a raw DOM style per mousemove
    // and doesn't touch any Angular-bound state, so there's no reason for it to
    // trigger a full change-detection pass on every mouse movement in the app.
    this.ngZone.runOutsideAngular(() => {
      this.guideUnlisten = this.renderer.listen('document', 'mousemove', (e: MouseEvent) => {
        this.pointerY = e.clientY;
        if (this.guideEl) this.renderer.setStyle(this.guideEl, 'top', `${e.clientY}px`);
      });
    });
  }

  private toggleReadingMask(active: boolean): void {
    this.maskTopEl = this.reconcileEffectEl('kx-reading-mask-top', active, ['kx-reading-mask-panel']);
    this.maskBottomEl = this.reconcileEffectEl('kx-reading-mask-bottom', active, ['kx-reading-mask-panel']);
    if (!this.maskTopEl || !this.maskBottomEl) {
      this.maskUnlisten?.();
      this.maskUnlisten = null;
      return;
    }
    this.positionMask(this.pointerY);
    if (this.maskUnlisten) return;
    // Same reasoning as the reading guide above — keep this out of Angular's zone.
    this.ngZone.runOutsideAngular(() => {
      this.maskUnlisten = this.renderer.listen('document', 'mousemove', (e: MouseEvent) => {
        this.pointerY = e.clientY;
        this.positionMask(e.clientY);
      });
    });
  }

  /** Centres the mask's clear band on `centerY`. Both panels get an explicit geometry every
   * time this runs, which is why the stylesheet's initial values are free to park them
   * off-screen — the old defaults left the lower panel covering the whole viewport in 75%
   * black on every page load that restored the setting (#201077). */
  private positionMask(centerY: number): void {
    if (!this.maskTopEl || !this.maskBottomEl) return;
    const viewport = window.innerHeight;
    const clamped = Math.min(Math.max(centerY, 0), viewport);
    this.renderer.setStyle(this.maskTopEl, 'height', `${Math.max(0, clamped - MASK_APERTURE_HALF)}px`);
    this.renderer.setStyle(this.maskBottomEl, 'top', `${Math.min(viewport, clamped + MASK_APERTURE_HALF)}px`);
  }
}